import { useState, useMemo } from "react";

const MatchingQuestion = ({ question, answer, quizType, onMatchingChange, showFeedback = false }) => {
  const [draggingItem, setDraggingItem] = useState(null);

  const [matchedPairs, setMatchedPairs] = useState(
    answer?.matching_answers?.reduce((acc, match) => {
      acc[match.matching_pair] = match.selected_right;
      return acc;
    }, {}) || {}
  );

  const shuffledRightItems = useMemo(() => {
    const rightItems = question.matching_pair.map(pair => pair.right);
    for (let i = rightItems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rightItems[i], rightItems[j]] = [rightItems[j], rightItems[i]];
    }
    return rightItems;
  }, [question.matching_pair]);

  const allPairsMatched = useMemo(() => {
    return question.matching_pair.every(pair => matchedPairs[pair.id]);
  }, [matchedPairs, question.matching_pair]);

  const isMatchCorrect = (pairId, selectedRight) => {
    const pair = question.matching_pair.find(p => p.id === pairId);
    return pair && pair.right === selectedRight;
  };

  const correctMatchesCount = useMemo(() => {
    if (quizType !== "learning") return 0;
    return Object.entries(matchedPairs).reduce((count, [pairId, selectedRight]) => {
      return count + (isMatchCorrect(pairId, selectedRight) ? 1 : 0);
    }, 0);
  }, [matchedPairs, question.matching_pair, quizType]);

  const handleDragStart = (item) => {
    setDraggingItem(item);
  };

  const handleDrop = (pairId) => {
    if (draggingItem) {
      const newMatchedPairs = { ...matchedPairs, [pairId]: draggingItem };
      setMatchedPairs(newMatchedPairs);
      onMatchingChange(pairId, draggingItem);
      setDraggingItem(null);
    }
  };

  const isItemMatched = (item) => {
    return Object.values(matchedPairs).includes(item);
  };

  const resetMatches = () => {
    if(quizType == 'learning' && showFeedback ){ // Prevent reset in learning mode with feedback
      return
    }
    
    setMatchedPairs({});
    question.matching_pair.forEach(pair => onMatchingChange(pair.id, ""));
  };

  const removeMatch = (pairId) => {
    const newMatchedPairs = { ...matchedPairs };
    delete newMatchedPairs[pairId];
    setMatchedPairs(newMatchedPairs);
    onMatchingChange(pairId, "");
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Left Column */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700">Match these items:</h3>
          {question.matching_pair.map(pair => (
            <div
              key={pair.id}
              className={`p-4 border rounded-lg flex items-center justify-between ${
                matchedPairs[pair.id]
                  ? quizType === "learning" && showFeedback
                    ? isMatchCorrect(pair.id, matchedPairs[pair.id])
                      ? "bg-green-50 border-green-300"
                      : "bg-red-50 border-red-300"
                    : "bg-primary/5 border-primary"
                  : "bg-white border-gray-200"
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(pair.id)}
            >
              <span className="font-medium">{pair.left}</span>
              {matchedPairs[pair.id] ? (
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      quizType === "learning" && showFeedback
                        ? isMatchCorrect(pair.id, matchedPairs[pair.id])
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                        : "bg-primary/10"
                    }`}
                  >
                    {matchedPairs[pair.id]}
                    {quizType === "learning" && showFeedback && (
                      isMatchCorrect(pair.id, matchedPairs[pair.id]) ? (
                        <span className="ml-1">✓</span>
                      ) : (
                        <span className="ml-1">✗</span>
                      )
                    )}
                  </span>
                  <button className="text-gray-400 hover:text-gray-600" onClick={() => removeMatch(pair.id)}>
                    ✕
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 min-w-[100px] h-[36px] flex items-center justify-center text-gray-400">
                  Drop here
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700">Drag from here:</h3>
          <div className="grid grid-cols-1 gap-2">
            {shuffledRightItems.map((item, idx) => (
              <div
                key={`right-${idx}`}
                draggable={!isItemMatched(item)}
                onDragStart={() => handleDragStart(item)}
                className={`p-3 border rounded-lg cursor-grab transition-all ${
                  isItemMatched(item)
                    ? "opacity-50 bg-gray-100 cursor-not-allowed"
                    : "hover:shadow-md hover:border-primary/30 active:cursor-grabbing"
                }`}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback Message */}
      {quizType === "learning" && showFeedback && allPairsMatched && (
        <div
          className={`p-4 rounded-lg mt-4 ${
            correctMatchesCount === question.matching_pair.length
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-amber-50 border border-amber-200 text-amber-800"
          }`}
        >
          <div className="font-medium mb-1">
            {correctMatchesCount === question.matching_pair.length
              ? "Perfect! All matches are correct."
              : `You got ${correctMatchesCount} out of ${question.matching_pair.length} matches correct.`}
          </div>
          {correctMatchesCount !== question.matching_pair.length && (
            <p>Review the highlighted matches and try again.</p>
          )}
        </div>
      )}

      {/* Reset Button */}
      {Object.keys(matchedPairs).length > 0 && (
        <button className="btn btn-outline btn-sm self-start mt-2" onClick={resetMatches}>
          Reset All Matches
        </button>
      )}

      {/* Fallback for Mobile */}
      <div className="md:hidden mt-4">
        <h3 className="font-medium text-gray-700 mb-2">Or select from dropdown:</h3>
        {question.matching_pair.map((pair, index) => (
          <div key={index} className="flex items-center mb-4 gap-4">
            <span
              className={`w-32 font-semibold ${
                quizType === "learning" && showFeedback && matchedPairs[pair.id]
                  ? isMatchCorrect(pair.id, matchedPairs[pair.id])
                    ? "text-green-700"
                    : "text-red-700"
                  : ""
              }`}
            >
              {pair.left}
              {quizType === "learning" &&
                showFeedback &&
                matchedPairs[pair.id] &&
                (isMatchCorrect(pair.id, matchedPairs[pair.id]) ? (
                  <span className="ml-1 text-green-600">✓</span>
                ) : (
                  <span className="ml-1 text-red-600">✗</span>
                ))}
            </span>
            <select
              className={`select w-full max-w-xs ${
                quizType === "learning" && showFeedback && matchedPairs[pair.id]
                  ? isMatchCorrect(pair.id, matchedPairs[pair.id])
                    ? "select-success"
                    : "select-error"
                  : "select-bordered"
              }`}
              value={matchedPairs[pair.id] || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const updated = { ...matchedPairs, [pair.id]: value };
                  setMatchedPairs(updated);
                  onMatchingChange(pair.id, value);
                } else {
                  removeMatch(pair.id);
                }
              }}
            >
              <option value="">Select match</option>
              {shuffledRightItems.map((item, idx) => (
                <option
                  key={idx}
                  value={item}
                  disabled={isItemMatched(item) && matchedPairs[pair.id] !== item}
                >
                  {item}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MatchingQuestion;